// Flag types indicate the status of the flag.
// The ordering of this enum is also used to determine the order in which the flags appear in the admin dashboard
export enum FlagType {
  Product = 'Product',
  Configuration = 'Configuration',
  // Beta flags should be removed once the feature is released
  Beta = 'Beta',
  Deprecated = 'Deprecated',
  // Alpha or in development
  Alpha = 'Alpha',
  // Configurations, but lesser used
  Tweaks = 'Tweaks',
  // Customer specific flags.
  Customer = 'Customer',
  // Performance related flags for features that need to be tweaked to be performant on some orgs.
  Performance = 'Performance',
  // Internal flags for FOSSA internal use only (e.g., super admin tooling)
  Internal = 'Internal',
}

export const flagTypeDescriptions: Record<FlagType, string> = {
  [FlagType.Product]: 'Full products in FOSSA (Released)',
  [FlagType.Configuration]: 'Configuration options for FOSSA organizations',
  [FlagType.Beta]: 'Stable but officially unreleased features',
  [FlagType.Deprecated]: 'Deprecated features in FOSSA',
  [FlagType.Alpha]: 'In development or unstable features',
  [FlagType.Tweaks]: 'Tweaks to FOSSA features',
  [FlagType.Customer]: 'Flags meant for specific customers',
  [FlagType.Performance]:
    'Performance related flags for features that need to be tweaked to be performant on some orgs',
  [FlagType.Internal]: 'Internal flags for FOSSA internal use only',
};

// All flags added must be added and documented to the `flagDescriptions`
// object below. This must be actively maintained by engineers
export const AMAZON_LINUX_SUPPORT = 'amazonLinuxEnabled';
export const ARCHIVE_AND_LICENSE_UPLOAD = 'archiveAndLicenseUpload';
export const CLI_DEFAULT_TO_FIRST_PARTY_SCANS = 'cliDefaultToFirstPartyScans';
export const CLI_LICENSE_SCAN_REQUIRE_FULL_FILE_UPLOADS = 'cliLicenseScanRequireFullFileUploads';
export const DEDUPE_LOCATORS_IN_PLAINTEXT_REPORTS = 'dedupeLocatorsInPlainTextReports';
export const DEPENDENCY_PATH_GLOBAL_ISSUES_REPORT = 'dependencyPathGlobalIssuesReport'; // This is just for large orgs
export const DISABLE_ORG_ALERT_BANNER = 'disableOrgAlertBanner';
export const EXCLUDES_DEPENDENCY_PATHS_FROM_ISSUES_CSV_EXPORT = 'excludeDependencyPathsFromIssuesCsvExport';
export const FILE_PATH_IN_REPORTS = 'filePathInReports';
export const LDAP_AUTHENTICATION = 'ldapAuthentication';
export const SCIM_PROVISIONING = 'scimProvisioning';
export const QUALITY = 'quality';
export const REQUIRE_POLICY_ON_UPLOAD = 'requirePolicyOnUpload';
export const REQUIRE_TEAM_ON_UPLOAD = 'requireTeamOnUpload';
export const SECURITY = 'security';
export const TEAM_UNIQUE_ID = 'teamUniqueId';
export const TEAM_UNIQUE_ID_IN_SAML = 'teamUniqueIdInSAML';
export const VENDORED_DEPENDENCY_DEFAULT_TO_ARCHIVE_UPLOAD = 'vendoredDependencyDefaultToArchiveUpload';
export const VULN_ADMIN = 'vulnAdmin';
export const SECURITY_PDF_REPORTS = 'securityPDFReports';
export const SBOM_PORTAL = 'sbomPortal';
export const SBOM_PORTAL_PRIVATE = 'sbomPortalPrivate';
export const RELIABLE_NEW_ISSUE_NOTIFICATIONS = 'reliableNewIssueNotifications';
export const REPORT_COPYRIGHT_SANITIZATION = 'reportCopyrightSanitization';
export const MANUAL_MALWARE = 'manualMalware';
export const ORG_DELETION = 'orgDeletion';
export const ISSUES_MALWARE = 'issuesMalware';
export const DISABLE_MANUAL_DEPENDENCIES_REVAMP = 'disableManualDependenciesRevamp';
export const DISABLE_MANUAL_DEPENDENCIES_TEST_VALIDITY = 'disableManualDependenciesTestValidity';
export const FUZZY_DEP_SEARCH = 'fuzzyDepSearch';
export const DYNAMIC_GITLAB_INSTANCES = 'dynamicGitlabInstances';
export const MULTI_ORG = 'multiOrg';
export const VCS_SERVICE_IMPORT = 'vcsServiceImport';

/**
 * Turns off the Deps V2 rewrite of the Global Package Bundle Report and uses the old version.
 */
export const DISABLE_ORG_BOM_V2 = 'disableOrgBomV2';
export const ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS = 'enableDownloadingLargeProjectLicensingReports';

/**
 * Snippet feature flags
 */
export const SNIPPET_DETECTION = 'snippetDetection';

/**
 * Vendored Dependency feature flags
 */
export const VENDORED_DEPENDENCY_DETECTION = 'vendoredDependencyDetection';

/**
 * Project Summary V2 feature flags
 */
export const PROJECT_SUMMARY_V2 = 'projectSummaryV2';

/**
 * DepsV3 Feature Flags
 */
export const BLOCK_DEPLOCK_WRITES = 'blockDeplockWrites';
export const BLOCK_DEPLOCK_READS = 'blockDeplockReads';

/**
 * Sparkle feature flags
 */
export const CPE_SEARCH_USE_SPARKLE = 'cpeSearchUseSparkle';
export const CPE_SEARCH_REVERSE_WILDCARD = 'cpeSearchReverseWildcard';
export const DISABLE_SPARKLE_BASED_BUILD_CACHING = 'disableSparkleBasedBuildCaching';

/**
 * Legacy Dependency Edit Modal Feature Flag
 * This is intended to be a TEMPORARY flag to alleviate some customer friction.
 * See https://github.com/fossas/FOSSA/pull/16861 for more details.
 */
export const LEGACY_DEP_EDIT_MODAL = 'legacyDepEditModal';

/* Hidden feature flags. These must be manually added to the database, and are not
 * available to be added via the admin panel. To add into the admin UI, you must
 * add below into `flagDescriptions`
 */
// UI for editing of Raw Vulns
export const RAW_VULN_EDIT = 'rawVulnEdit';
// Include all dependencies and licenses in the live report
export const FULL_LIVE_REPORT = 'fullLiveReport';
// Enables the Package Observability Inventory Feature
export const PACKAGE_INVENTORY = 'packageInventoryV2';
export const PACKAGE_INVENTORY_CACHE = 'packageInventoryCache';
export const DISABLE_PACKAGE_VULN_FILTER_CACHE = 'disablePackageVulnFilterCache';
// We want to selectively add this FF to certain orgs that do not want their users importing projects via the UI.
export const DISABLE_ADD_PROJECT_BUTTON = 'disableAddProjectButton';
export const GLOBAL_LICENSE_CORRECTIONS = 'globalLicenseCorrections';
// Changes "Ignore" to "Resolve" in some FE components. Please use this sparingly.
export const IGNORE_RESOLVE_RENAME = 'ignoreResolveRename';
// PDF Compliance Reports V2
export const DISABLE_PDF_COMPLIANCE_REPORTS_V2 = 'disablePDFComplianceReportsV2';
// Marks whether the issue-snapshot backfill has been finished for this org
export const BACKFILL_ISSUE_SNAPSHOTS_COMPLETE = 'backfillIssueSnapshotsComplete';
// User defined dependencies from the CLI don't require licenses, and can create a lot of noise and high number of issues
// if used in a specific way (binary scannning for example). This flag doesn't flag issues for any deps of fetcher `user`
export const SKIP_UNLICENSED_ISSUES_FOR_USER_DEFINED_DEPENDENCIES = 'skipUnlicensedIssuesForUserDefinedDependencies';
export const DISABLE_TASK_LOGS = 'disableTaskLogs';
// When enabled globally, falls back to the legacy multi-query chunked code path for IssueSnapshotForProject
// instead of the optimized single-SQL-query approach. Use as an escape hatch if the optimized query causes issues.
export const USE_LEGACY_ISSUE_SNAPSHOT_QUERY = 'useLegacyIssueSnapshotQuery';
// Per-org opt-in (off = legacy chunked). Computes `GET /api/issue_counts` breakdowns in one SQL
// statement (two DISTINCT ON as-of CTEs whose issue-id arrays are diffed per tuple with EXCEPT) instead
// of fetching every tuple's id arrays into Node and diffing there. Applies to EVERY breakdowns request --
// filtered or not; the filters become predicates on the statement. This is the read-side counterpart
// to — not a reuse of — USE_LEGACY_ISSUE_SNAPSHOT_QUERY above. The chunked machinery itself stays
// regardless: `ExportIssueSnapshots` consumes the raw id arrays and never moves to this statement.
export const GLOBAL_ISSUE_SNAPSHOT_BREAKDOWNS_SQL_DIFF = 'globalIssueSnapshotBreakdownsSqlDiff';
// Per-org opt-in (off = legacy CTE). Gates the fast path for `getIssueSnapshots` — the per-day `counts`
// series of `GET /api/issue_counts` only, used only for fully unfiltered requests; breakdowns are gated
// separately by GLOBAL_ISSUE_SNAPSHOT_BREAKDOWNS_SQL_DIFF above.
export const GLOBAL_ISSUE_SNAPSHOT_TREND = 'globalIssueSnapshotTrend';
// Per-org opt-in (off = the legacy BOOL_OR aggregate over every historical build). The two paths
// disagree for a locator whose newest build is terminal while an older build is still non-terminal
// and last finished over the lookback window ago: the legacy path calls that dependency pending,
// the latest-build path calls it settled.
export const LATEST_BUILD_STEADY_STATE_QUERY = 'latestBuildSteadyStateQuery';
export const VULN_RANGES_EXPORT = 'vulnRangesExport';
// Hard code a CycloneDX standard + attestation to CycloneDX JSON SBOMs
export const HARD_CODED_CYCLONEDX_ATTESTATION = 'hardCodedCycloneDXAttestation';
// Enables configuring API webhooks for issue notifications
export const API_WEBHOOK_NOTIFICATIONS = 'apiWebhookNotifications';
// Enables the FOSSA Command Palette (CMD + K/fast search/etc.)
export const COMMAND_PALETTE = 'commandPalette';
// Enables the integration with Code Sentry
export const BINARY_DECOMPOSITION = 'binaryDecomposition';
export const US_BANK_FIX_PLAN_HEADERS = 'usBankFixPlanHeaders';
//  Note: this is a Ford-only feature, which is why it's hidden.
export const TEAM_GROUPS = 'teamGroups';
// Gates the fossabot dependency-upgrade PR feature
export const FOSSABOT_DEP_UPGRADE_PRS = 'fossabotDepUpgradePRs';
// Enables the SBOM Sharing feature
export const SBOM_SHARING = 'sbomSharing';
// Deliberately hidden flag - used for the Mossdeep project
export const MOSSDEEP = 'mossdeep';
/* On-prem escape hatch that hides the "Unknown Dependencies" banner on the reports page and the
 * "Unknown Dependencies" box on the project and release group summaries.
 * Meant to be enabled globally via `featureManager:enabledFeatureFlags`, for deployments
 * where most unknown dependencies are proprietary code we can never resolve. Never enable
 * this for cloud: it suppresses a real data-quality signal. */
export const UNSAFE_HIDE_UNKNOWN_DEPS_BANNER_IN_REPORTS = 'unsafeHideUnknownDepsBannerInReports';

/**
 * This is the Feature Flag that will be checked when deciding whether or not to restrict security features.
 */
export const FULL_SECURITY = 'fullSecurity';

/**
 * This is the Feature Flag that will be checked when deciding whether or not to restrict quality features.
 */
export const FULL_QUALITY = 'fullQuality';

/**
 * This flag determines if the fossabot promos should be visible.
 */
export const HIDE_FOSSABOT_PROMO = 'hideFossabotPromo';

/**
 * Gates the redesigned Global Issue CSV Export card (team-group / release-group scope, the issues-list /
 * daily-counts include flags, and the metrics preview). When off, the classic export card is shown.
 */
export const TEAM_GROUP_ISSUE_REPORT = 'teamGroupIssueReport';

/**
 * Gates dependency-metadata-driven auto-ignore: fetching CLI-collected linking/distribution
 * metadata from Sparkle and using it to auto-ignore vulnerability and licensing issues on
 * non-distributed dependencies. When off, no Sparkle call is made and no issues are stamped.
 */
export const DEP_METADATA_AUTO_IGNORE = 'depMetadataAutoIgnore';

/**
 * Legal Agent (epic CORE-7623). Gates every M1 surface for the org: the LC++ license conclusion path,
 * the Agents subnav, and automatic false-positive license issue ignores. Requires License Conclusions
 * to be enabled for the org; the flag alone does nothing.
 */
export const LEGAL_AGENT = 'legalAgent';

/**
 * Shadow-reads the Issue Inbox vuln list against index-service alongside the authoritative
 * Postgres read. Postgres is always returned; the index leg runs detached.
 */
export const ISSUE_INBOX_INDEX_SHADOW = 'issueInboxIndexShadow';

/**
 * Serves the Issue Inbox list from index-service instead of Postgres. Wins over SHADOW above when
 * both are on, so an org never double-reads.
 */
export const ISSUE_INBOX_INDEX_SERVE = 'issueInboxIndexServe';
export const SPDX_3_EXPORT = 'spdx3Export';

/**
 * Shadow-reads the Packages page against the package-usage index alongside the authoritative
 * Postgres read (modules/PackageCacheManager). Postgres is always returned; the index leg runs
 * detached and the difference is recorded.
 */
export const PACKAGE_CACHE_INDEX_SHADOW = 'packageCacheIndexShadow';

/**
 * Serves the Packages page from the package-usage index instead of Postgres, falling back to
 * Postgres (counted) if the index leg fails. Wins over SHADOW above when both are on, so an org
 * never double-reads.
 */
export const PACKAGE_CACHE_INDEX_SERVE = 'packageCacheIndexServe';

type FlagDescription = {
  displayName: string;
  description: string;
  type: FlagType;
};

// Flag names and their descriptions to show in the UI
// NOTE: these are loosely ordered by popularity/time of creation
export const flagDescriptions: Record<string, FlagDescription> = {
  [QUALITY]: {
    displayName: 'Quality',
    description:
      'Turns limited quality features on for the organization. For full features, also enable Full Quality. If hidden, that means the subscription tier has it included already.',
    type: FlagType.Product,
  },
  [FULL_QUALITY]: {
    displayName: 'Full Quality',
    description:
      'Turns full quality features on for the organization. Without this, the org cannot customize quality policies.',
    type: FlagType.Product,
  },
  [SECURITY]: {
    displayName: 'Security',
    description: 'Turns limited security features on for the organization. (Legacy Billing)',
    type: FlagType.Product,
  },
  [FULL_SECURITY]: {
    displayName: 'Full Security',
    description: 'Turns full security features on for the organization. (Legacy Billing)',
    type: FlagType.Product,
  },
  [ARCHIVE_AND_LICENSE_UPLOAD]: {
    displayName: 'Archive + CLI License Scanning',
    description: 'Turns the archive upload functionality on, which includes CLI License Scanning.',
    type: FlagType.Product,
  },
  [DEPENDENCY_PATH_GLOBAL_ISSUES_REPORT]: {
    displayName: 'Dependency Path in Global Issues Report',
    description:
      'Adds the Dependency Path column to Global Issues CSV Report. The process is very expensive for non-provided projects.',
    type: FlagType.Tweaks,
  },
  [DISABLE_ORG_ALERT_BANNER]: {
    displayName: 'Disable Org Alert Banner',
    description: 'Disable super user external org warning banner when outside of the fossa org',
    type: FlagType.Internal,
  },
  [LDAP_AUTHENTICATION]: {
    displayName: 'LDAP Authentication',
    description: 'Allows organizations to sign in to FOSSA via LDAP.',
    type: FlagType.Configuration,
  },
  [SCIM_PROVISIONING]: {
    displayName: 'SCIM Provisioning',
    description: 'Allows organizations to provision and manage users via SCIM.',
    type: FlagType.Product,
  },
  [FILE_PATH_IN_REPORTS]: {
    displayName: 'File Path in Reports',
    description: 'Adds file path information to the reporting UI.',
    type: FlagType.Tweaks,
  },
  [TEAM_UNIQUE_ID]: {
    displayName: 'Team Unique ID',
    description: 'Adds ability to set a unique ID for a team across your organization.',
    type: FlagType.Customer,
  },
  [TEAM_GROUPS]: {
    displayName: 'Team Groups',
    description: 'Adds the Team Groups feature, which allows you to group teams.',
    type: FlagType.Customer,
  },
  [VENDORED_DEPENDENCY_DEFAULT_TO_ARCHIVE_UPLOAD]: {
    displayName: 'Default to Archive Uploads',
    description: 'CLI defaults to Archive Uploads instead of CLI-side license scans for vendored dependency builds.',
    type: FlagType.Configuration,
  },
  [CLI_LICENSE_SCAN_REQUIRE_FULL_FILE_UPLOADS]: {
    displayName: 'Full-file uploads for CLI License scans',
    description:
      'CLI-side license scans upload full file contents instead of the match string. This uploads customer code to FOSSA\'s servers.',
    type: FlagType.Tweaks,
  },
  [CLI_DEFAULT_TO_FIRST_PARTY_SCANS]: {
    displayName: 'First-party scans for CLI',
    description: 'Default to license scanning the directory being analyzed by the CLI',
    type: FlagType.Configuration,
  },
  [DEDUPE_LOCATORS_IN_PLAINTEXT_REPORTS]: {
    displayName: 'De-dupe locators in Plaintext Reports',
    description:
      'De-duplicates matching locators from dependencies that are both direct and transitive at the same time and keeps only the direct one in plaintext reports for Projects & Release Groups',
    type: FlagType.Configuration,
  },
  [EXCLUDES_DEPENDENCY_PATHS_FROM_ISSUES_CSV_EXPORT]: {
    displayName: 'Exclude Dependency Paths from Issues Csv Export',
    description: `If enabled, dependency paths will not be included when downloading csv from global issues page.`,
    type: FlagType.Performance,
  },
  [REQUIRE_POLICY_ON_UPLOAD]: {
    displayName: 'Require Policy on Upload',
    description: 'Reject new projects uploaded from the CLI without an explicitly set policy.',
    type: FlagType.Configuration,
  },
  [REQUIRE_TEAM_ON_UPLOAD]: {
    displayName: 'Require Team on Upload',
    description: 'Reject new projects uploaded from the CLI and UI without an explicitly set team.',
    type: FlagType.Configuration,
  },
  [DISABLE_ADD_PROJECT_BUTTON]: {
    displayName: 'Disable Add Project Button',
    description: 'Disables the Add Project button on the projects dashboard.',
    type: FlagType.Configuration,
  },
  [DISABLE_PDF_COMPLIANCE_REPORTS_V2]: {
    displayName: 'Disable PDF Compliance Reports V2',
    description: 'Display PDF compliance reports using the old format.',
    type: FlagType.Product,
  },
  [SECURITY_PDF_REPORTS]: {
    displayName: 'Security PDF Reports',
    description:
      'Enables global and project level security PDF reports. Disabled by default in favor of Issue History reports',
    type: FlagType.Deprecated,
  },
  [SBOM_PORTAL]: {
    displayName: 'SBOM Portal',
    description: 'Allows an organization to create and publish to their own SBOM Portal',
    type: FlagType.Alpha,
  },
  [SBOM_PORTAL_PRIVATE]: {
    displayName: 'SBOM Portal Private',
    description: 'Allows an organization to configure a private SBOM Portal',
    type: FlagType.Product,
  },
  [DISABLE_ORG_BOM_V2]: {
    displayName: 'Disable Org Bom V2',
    description: 'Disables the Deps V2 rewrite of the Global Package Bundle Report and uses the old version.',
    type: FlagType.Configuration,
  },
  [LATEST_BUILD_STEADY_STATE_QUERY]: {
    displayName: 'Latest-Build Steady State Query',
    description:
      'Determines dependency build status from the most recent build per locator instead of aggregating every historical build. Off falls back to the legacy aggregate.',
    type: FlagType.Performance,
  },
  [VULN_RANGES_EXPORT]: {
    displayName: 'Vuln Ranges Export',
    description: 'Enables exporting of vulnerability version ranges to a CSV file.',
    type: FlagType.Customer,
  },
  [GLOBAL_ISSUE_SNAPSHOT_BREAKDOWNS_SQL_DIFF]: {
    displayName: 'Issue Snapshot Breakdowns SQL Diff',
    description:
      'Computes GET /api/issue_counts breakdowns in a single SQL statement instead of diffing issue-id arrays in Node, for every request (filtered or not). Off falls back to the original chunked path.',
    type: FlagType.Performance,
  },
  [GLOBAL_ISSUE_SNAPSHOT_TREND]: {
    displayName: 'Global Issue Snapshot Trend',
    description:
      'Per-org opt-in for the global (org-wide) single UNION ALL fast path for the GET /api/issue_counts trend (counts) series, used only for fully unfiltered requests. Off falls back to the legacy CTE-based query.',
    type: FlagType.Performance,
  },
  [ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS]: {
    displayName: 'Enable Downloading Large Project Licensing Reports',
    description:
      'Enables directly downloading large project licensing reports (which can be slow and potentially broken).',
    type: FlagType.Deprecated,
  },
  [HARD_CODED_CYCLONEDX_ATTESTATION]: {
    displayName: 'SBOM Attestations',
    description: 'Adds real attestations for hardcoded claims to SBOMs',
    type: FlagType.Customer,
  },
  [COMMAND_PALETTE]: {
    displayName: 'Command Palette',
    description: 'Enables the FOSSA Command Palette (CMD + K/fast search/etc.)',
    type: FlagType.Alpha,
  },
  [BINARY_DECOMPOSITION]: {
    displayName: 'Binary Decomposition through CodeSentry',
    description:
      'Enables Binary Decomposition of uploaded archives through CodeSentry, also turns on the Inventory UI.',
    type: FlagType.Product,
  },
  [FOSSABOT_DEP_UPGRADE_PRS]: {
    displayName: 'Fossabot Dependency Upgrade PRs',
    description: 'Enables fossabot to open automated dependency upgrade pull requests.',
    type: FlagType.Alpha,
  },
  [SBOM_SHARING]: {
    displayName: 'SBOM Sharing',
    description: 'Enables the SBOM Sharing feature',
    type: FlagType.Customer,
  },
  [SNIPPET_DETECTION]: {
    displayName: 'Snippet Detection',
    description: 'Enables the Snippet Detection feature for detecting snippets in your codebase.',
    type: FlagType.Beta,
  },
  [VENDORED_DEPENDENCY_DETECTION]: {
    displayName: 'Vendored Dependencies',
    description: 'Enables the Vendored Dependency feature for detecting vendored dependencies in your codebase.',
    type: FlagType.Beta,
  },
  [PROJECT_SUMMARY_V2]: {
    displayName: 'Project Summary V2',
    description: 'Enables V2 of the Project Summary Page.',
    type: FlagType.Alpha,
  },
  [CPE_SEARCH_USE_SPARKLE]: {
    displayName: 'Sparkle CPE Search',
    description: 'Search for CPE matches in Sparkle instead of the vulns service',
    type: FlagType.Beta,
  },
  [CPE_SEARCH_REVERSE_WILDCARD]: {
    displayName: 'CPE Reverse Wildcard Search',
    description: 'Requires Sparkle CPE Search. Performs reverse wildcard matching on CPEs.',
    type: FlagType.Product,
  },
  [RELIABLE_NEW_ISSUE_NOTIFICATIONS]: {
    displayName: 'Reliable New Issue Notifications',
    description:
      'For project scans, determine new-issue notifications from a revision-scan diff against the '
      + 'previous default-branch scan (instead of createdAt timestamps), and gate sending on whether '
      + 'the scanned revision is actually on the default branch (ancestry check) rather than on it '
      + 'being the project\'s last_analyzed_revision. Project-group scans keep ID-based newness.',
    type: FlagType.Alpha,
  },
  [REPORT_COPYRIGHT_SANITIZATION]: {
    displayName: '[Hack] Report Copyright Sanitization',
    description: 'Sanitizes copyright notices in reports. This is a hack to unblock some customers.',
    type: FlagType.Customer,
  },
  [TEAM_UNIQUE_ID_IN_SAML]: {
    displayName: 'Team Unique ID in SAML',
    description: 'Set the team unique id when creating a new team via SAML log in.',
    type: FlagType.Customer,
  },
  [MANUAL_MALWARE]: {
    displayName: 'Manual Malware',
    description: 'Allow super admin to manually create, update, or delete malware reports.',
    type: FlagType.Internal,
  },
  [ORG_DELETION]: {
    displayName: 'Org Deletion',
    description: 'Allow super admin to schedule, cancel, and view organization deletions in Internal Tools.',
    type: FlagType.Internal,
  },
  [ISSUES_MALWARE]: {
    displayName: 'Issues: Malware',
    description: 'Flag malicious package locators as "Malware"',
    type: FlagType.Beta,
  },
  [DISABLE_SPARKLE_BASED_BUILD_CACHING]: {
    displayName: 'Disable Sparkle Based Build Caching',
    description:
      'Don\'t skip queueing dependencies for builds if one has completed recently. On by default for all orgs.',
    type: FlagType.Configuration,
  },
  [LEGACY_DEP_EDIT_MODAL]: {
    displayName: 'Legacy Dependency Edit Modal',
    description: 'Disables the new Dependency Drawer and uses the old edit UI (deprecated soon)',
    type: FlagType.Customer,
  },
  [DISABLE_MANUAL_DEPENDENCIES_REVAMP]: {
    displayName: 'Disable Manual Dependencies Revamp',
    description: 'Disables the new user interface and experience for adding manual dependencies.',
    type: FlagType.Deprecated,
  },
  [DISABLE_MANUAL_DEPENDENCIES_TEST_VALIDITY]: {
    displayName: 'Disable Manual Dependencies Test Validity',
    description: 'Disables testing the validity of manual dependencies before they are added.',
    type: FlagType.Deprecated,
  },
  [FUZZY_DEP_SEARCH]: {
    displayName: 'Fuzzy Dep Search (Manual Dep Typeahead)',
    description: 'Enables typeahead-based package and version selection in the Add Dependency drawer for supported registries.',
    type: FlagType.Alpha,
  },
  [HIDE_FOSSABOT_PROMO]: {
    displayName: 'Hide fossabot Promotions',
    description: 'Disables all fossabot promotions throughout the app including banner ads and in line suggestions.',
    type: FlagType.Customer,
  },
  [DYNAMIC_GITLAB_INSTANCES]: {
    displayName: 'Dynamic GitLab Instances',
    description: 'Allows users to add and manage their own GitLab instances in FOSSA.',
    type: FlagType.Customer,
  },
  [MULTI_ORG]: {
    displayName: 'Multi-Organization Support',
    description: 'Allows users with the same email to belong to multiple organizations. SaaS only.',
    type: FlagType.Alpha,
  },
  [BLOCK_DEPLOCK_WRITES]: {
    displayName: 'Block DependencyLock Writes',
    description: 'Disables writes to the DepLocks table. Toggle with care.',
    type: FlagType.Alpha,
  },
  [BLOCK_DEPLOCK_READS]: {
    displayName: 'Block DependencyLock Reads',
    description: 'Canary: skips remaining DepLocks reads and returns empty-table-equivalent results. Toggle with care.',
    type: FlagType.Alpha,
  },
  [VCS_SERVICE_IMPORT]: {
    displayName: 'VCS Service: Import Projects',
    description: 'Enables the vcs-service project import flow (import page and import endpoints).',
    type: FlagType.Alpha,
  },
  [TEAM_GROUP_ISSUE_REPORT]: {
    displayName: 'Team Group Issue Report',
    description:
      'Enables the redesigned Global Issue CSV Export card (team-group / release-group scope, include flags, and the metrics preview). When off, the classic export card is shown.',
    type: FlagType.Alpha,
  },
  [DEP_METADATA_AUTO_IGNORE]: {
    displayName: 'Dependency Metadata Auto-Ignore',
    description:
      'Uses CLI-collected dependency metadata (linking, distribution) from Sparkle to auto-ignore vulnerability and licensing issues on non-distributed dependencies. When off, zero behavioral delta.',
    type: FlagType.Alpha,
  },
  [LEGAL_AGENT]: {
    displayName: 'Legal Agent',
    description:
      'Enables the Legal Agent for the organization: the LC++ license conclusion path, the Agents subnav, and '
      + 'automatic false-positive license issue ignores. Requires License Conclusions to be enabled for the org.',
    type: FlagType.Alpha,
  },
  [ISSUE_INBOX_INDEX_SHADOW]: {
    displayName: 'Issue Inbox: Index Shadow Read',
    description:
      'Runs a detached, index-service-backed shadow read alongside the authoritative Postgres Issue Inbox list read for comparison telemetry. Never affects the response. Ships dark.',
    type: FlagType.Alpha,
  },
  [ISSUE_INBOX_INDEX_SERVE]: {
    displayName: 'Issue Inbox: Serve From Index',
    description:
      'Serves the Issue Inbox list from index-service instead of Postgres. Canary flag for the read-integration cutover; wins over the shadow flag when both are on. Ships dark.',
    type: FlagType.Alpha,
  },
  [PACKAGE_CACHE_INDEX_SHADOW]: {
    displayName: 'Packages Page: Index Shadow Read',
    description:
      'Runs a detached, package-usage-index-backed shadow read alongside the authoritative Postgres Packages page read for comparison telemetry. Never affects the response. Ships dark.',
    type: FlagType.Alpha,
  },
  [PACKAGE_CACHE_INDEX_SERVE]: {
    displayName: 'Packages Page: Serve From Index',
    description:
      'Serves the Packages page from the package-usage index instead of Postgres, falling back to Postgres if the index read fails. Canary flag for the read-integration cutover; wins over the shadow flag when both are on. Ships dark.',
    type: FlagType.Alpha,
  },
  [SPDX_3_EXPORT]: {
    displayName: 'SPDX 3.0 Export',
    description: 'Adds SPDX 3.0 (JSON-LD) as an SBOM report export format alongside SPDX 2.3.',
    type: FlagType.Alpha,
  },
} as const;
